import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Form, Alert, Spinner } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchEventById, createEvent, updateEvent, clearCurrentEvent, EventStatus } from '../redux/slices/eventsSlice';
import { eventsAPI } from '../services/eventsApi';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const STATUS_OPTIONS: { value: EventStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'in_progress', label: 'Live / In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'postponed', label: 'Postponed' },
];

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div style={{ marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
    <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>{title}</h2>
    {subtitle && <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{subtitle}</p>}
  </div>
);

const ToggleSwitch: React.FC<{
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ id, label, description, checked, onChange }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', borderRadius: 8,
    background: checked ? '#f0f9ff' : '#f8f9fc',
    border: `1px solid ${checked ? '#bae6fd' : '#e2e8f0'}`,
    marginBottom: 10, cursor: 'pointer',
  }} onClick={() => onChange(!checked)}>
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{label}</div>
      {description && <div style={{ fontSize: 12, color: '#64748b' }}>{description}</div>}
    </div>
    <div style={{
      width: 44, height: 24, borderRadius: 12,
      background: checked ? '#00579a' : '#cbd5e1',
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 3,
        left: checked ? 22 : 3,
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  </div>
);

const EventFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const dispatch = useAppDispatch();
  const { currentEvent, loading } = useAppSelector(s => s.events);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Image previews
  const [featuredFile, setFeaturedFile] = useState<File | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [featuredPreview, setFeaturedPreview] = useState('');
  const [posterPreview, setPosterPreview] = useState('');

  // Document uploads
  const [newDocs, setNewDocs] = useState<File[]>([]);

  const [form, setForm] = useState({
    title: '',
    category: '',
    summary: '',
    description: '',
    location: '',
    venue_name: '',
    is_virtual: false,
    virtual_link: '',
    start_date: '',
    end_date: '',
    status: 'draft' as EventStatus,
    capacity: '',
    registration_required: false,
    registration_deadline: '',
    is_free: true,
    price: '',
    currency: 'NAD',
    require_proof_of_payment: false,
    require_id_verification: false,
    require_facial_verification: false,
    require_manual_approval: false,
  });

  useEffect(() => {
    if (isEdit && id) {
      dispatch(fetchEventById(id));
    }
    return () => { dispatch(clearCurrentEvent()); };
  }, [id]);

  useEffect(() => {
    if (isEdit && currentEvent) {
      const toInput = (d: string | undefined) =>
        d ? new Date(d).toISOString().slice(0, 16) : '';

      setForm({
        title: currentEvent.title || '',
        category: currentEvent.category || '',
        summary: currentEvent.summary || '',
        description: currentEvent.description || '',
        location: currentEvent.location || '',
        venue_name: currentEvent.venue_name || '',
        is_virtual: Boolean(currentEvent.is_virtual),
        virtual_link: currentEvent.virtual_link || '',
        start_date: toInput(currentEvent.start_date),
        end_date: toInput(currentEvent.end_date),
        status: currentEvent.status,
        capacity: currentEvent.capacity?.toString() || '',
        registration_required: Boolean(currentEvent.registration_required),
        registration_deadline: toInput(currentEvent.registration_deadline),
        is_free: Boolean(currentEvent.is_free),
        price: currentEvent.price?.toString() || '',
        currency: currentEvent.currency || 'NAD',
        require_proof_of_payment: Boolean(currentEvent.require_proof_of_payment),
        require_id_verification: Boolean(currentEvent.require_id_verification),
        require_facial_verification: Boolean(currentEvent.require_facial_verification),
        require_manual_approval: Boolean(currentEvent.require_manual_approval),
      });

      if (currentEvent.featured_image)
        setFeaturedPreview(`${API_URL}/${currentEvent.featured_image}`);
      if (currentEvent.poster_image)
        setPosterPreview(`${API_URL}/${currentEvent.poster_image}`);
    }
  }, [currentEvent]);

  const set = (field: string, value: any) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleImageSelect = (
    file: File,
    setter: React.Dispatch<React.SetStateAction<File | null>>,
    previewSetter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setter(file);
    const reader = new FileReader();
    reader.onloadend = () => previewSetter(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.start_date) {
      setError('Title and start date are required.');
      return;
    }

    setSubmitting(true);
    setError('');

    const payload: Record<string, any> = {
      ...form,
      capacity: form.capacity ? Number(form.capacity) : null,
      price: form.price ? Number(form.price) : null,
    };

    try {
      let eventId = id;

      if (isEdit && id) {
        await dispatch(updateEvent({ id, data: payload })).unwrap();
      } else {
        const created = await dispatch(createEvent(payload)).unwrap();
        eventId = created.id;
      }

      // Upload images if selected
      if (featuredFile && eventId) {
        await eventsAPI.uploadImage(eventId, featuredFile, 'featured_image');
      }
      if (posterFile && eventId) {
        await eventsAPI.uploadImage(eventId, posterFile, 'poster_image');
      }
      // Upload new documents
      if (newDocs.length && eventId) {
        await eventsAPI.uploadDocuments(eventId, newDocs, 'document');
      }

      setSuccess(isEdit ? 'Event updated successfully!' : 'Event created successfully!');
      setTimeout(() => navigate(eventId ? `/events/${eventId}` : '/events'), 1000);
    } catch (err: any) {
      setError(typeof err === 'string' ? err : (err?.message || 'Something went wrong'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isEdit && loading && !currentEvent) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spinner animation="border" style={{ color: '#00579a' }} />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f8f9fc', minHeight: '100vh', paddingTop: 32, paddingBottom: 60 }}>
      <Container fluid style={{ maxWidth: 1000 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
          <button
            onClick={() => navigate('/events')}
            style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: '#64748b' }}
          >
            ← Back
          </button>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>
              {isEdit ? 'Edit Event' : 'Create Event'}
            </h1>
            {isEdit && currentEvent && (
              <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{currentEvent.title}</p>
            )}
          </div>
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <form onSubmit={handleSubmit}>
          <Row>
            {/* Left column — main content */}
            <Col lg={8}>
              {/* Basic Info */}
              <Card>
                <SectionHeader title="Basic Information" />
                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Title <span style={{ color: '#dc2626' }}>*</span></Form.Label>
                  <Form.Control
                    value={form.title}
                    onChange={e => set('title', e.target.value)}
                    placeholder="Event title"
                    style={inputStyle}
                    required
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Category</Form.Label>
                      <Form.Control
                        value={form.category}
                        onChange={e => set('category', e.target.value)}
                        placeholder="e.g. Conference, Workshop"
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Status</Form.Label>
                      <Form.Select value={form.status} onChange={e => set('status', e.target.value)} style={inputStyle}>
                        {STATUS_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label style={labelStyle}>Summary</Form.Label>
                  <Form.Control
                    as="textarea" rows={2}
                    value={form.summary}
                    onChange={e => set('summary', e.target.value)}
                    placeholder="Short description shown in listings"
                    style={inputStyle}
                  />
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label style={labelStyle}>Full Description</Form.Label>
                  <Form.Control
                    as="textarea" rows={5}
                    value={form.description}
                    onChange={e => set('description', e.target.value)}
                    placeholder="Detailed event description..."
                    style={inputStyle}
                  />
                </Form.Group>
              </Card>

              {/* Date & Location */}
              <Card>
                <SectionHeader title="Date & Location" />

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>Start Date & Time <span style={{ color: '#dc2626' }}>*</span></Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={form.start_date}
                        onChange={e => set('start_date', e.target.value)}
                        style={inputStyle}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>End Date & Time</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={form.end_date}
                        onChange={e => set('end_date', e.target.value)}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <ToggleSwitch
                  id="is_virtual"
                  label="Virtual Event"
                  description="This event takes place online"
                  checked={form.is_virtual}
                  onChange={v => set('is_virtual', v)}
                />

                {form.is_virtual ? (
                  <Form.Group className="mb-0 mt-2">
                    <Form.Label style={labelStyle}>Virtual Meeting Link</Form.Label>
                    <Form.Control
                      value={form.virtual_link}
                      onChange={e => set('virtual_link', e.target.value)}
                      placeholder="https://zoom.us/..."
                      style={inputStyle}
                    />
                  </Form.Group>
                ) : (
                  <Row className="mt-2">
                    <Col md={6}>
                      <Form.Group className="mb-0">
                        <Form.Label style={labelStyle}>Venue Name</Form.Label>
                        <Form.Control
                          value={form.venue_name}
                          onChange={e => set('venue_name', e.target.value)}
                          placeholder="e.g. Safari Hotel"
                          style={inputStyle}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-0">
                        <Form.Label style={labelStyle}>Location / Address</Form.Label>
                        <Form.Control
                          value={form.location}
                          onChange={e => set('location', e.target.value)}
                          placeholder="Full address"
                          style={inputStyle}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card>

              {/* Registration */}
              <Card>
                <SectionHeader title="Registration & Attendance" subtitle="Configure if and how people can register for this event" />

                <ToggleSwitch
                  id="registration_required"
                  label="Registration Required"
                  description="Allow people to sign up to attend"
                  checked={form.registration_required}
                  onChange={v => set('registration_required', v)}
                />

                {form.registration_required && (
                  <>
                    <Row className="mt-2">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label style={labelStyle}>Capacity</Form.Label>
                          <Form.Control
                            type="number" min={1}
                            value={form.capacity}
                            onChange={e => set('capacity', e.target.value)}
                            placeholder="Leave blank for unlimited"
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label style={labelStyle}>Registration Deadline</Form.Label>
                          <Form.Control
                            type="datetime-local"
                            value={form.registration_deadline}
                            onChange={e => set('registration_deadline', e.target.value)}
                            style={inputStyle}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div style={{ marginTop: 8 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Verification Requirements</p>
                      <ToggleSwitch
                        id="require_manual_approval"
                        label="Manual Approval Required"
                        description="Registrations stay 'pending' until you approve them in the CMS"
                        checked={form.require_manual_approval}
                        onChange={v => set('require_manual_approval', v)}
                      />
                      <ToggleSwitch
                        id="require_proof_of_payment"
                        label="Require Proof of Payment"
                        description="Attendees must upload proof of payment"
                        checked={form.require_proof_of_payment}
                        onChange={v => set('require_proof_of_payment', v)}
                      />
                      <ToggleSwitch
                        id="require_id_verification"
                        label="Require ID Document"
                        description="Attendees must upload a government-issued ID"
                        checked={form.require_id_verification}
                        onChange={v => set('require_id_verification', v)}
                      />
                      <ToggleSwitch
                        id="require_facial_verification"
                        label="Require Facial Image"
                        description="Attendees must upload a selfie / facial photo"
                        checked={form.require_facial_verification}
                        onChange={v => set('require_facial_verification', v)}
                      />
                    </div>
                  </>
                )}
              </Card>

              {/* Pricing */}
              <Card>
                <SectionHeader title="Pricing" />
                <ToggleSwitch
                  id="is_free"
                  label="Free Event"
                  description="No ticket price required"
                  checked={form.is_free}
                  onChange={v => set('is_free', v)}
                />
                {!form.is_free && (
                  <Row className="mt-2">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>Currency</Form.Label>
                        <Form.Select value={form.currency} onChange={e => set('currency', e.target.value)} style={inputStyle}>
                          {['NAD','USD','ZAR','EUR','GBP','BWP','ZMW'].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>Price</Form.Label>
                        <Form.Control
                          type="number" min={0} step="0.01"
                          value={form.price}
                          onChange={e => set('price', e.target.value)}
                          placeholder="0.00"
                          style={inputStyle}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Card>

              {/* Documents */}
              <Card>
                <SectionHeader title="Attachments" subtitle="Upload documents or files for this event" />

                {isEdit && currentEvent?.documents && currentEvent.documents.filter(d => d.file_type === 'document').length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Existing Attachments</p>
                    {currentEvent.documents.filter(d => d.file_type === 'document').map(doc => (
                      <div key={doc.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', background: '#f8f9fc', borderRadius: 6, marginBottom: 6,
                      }}>
                        <span style={{ fontSize: 13, color: '#374151' }}>📄 {doc.file_name}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div
                  style={{
                    border: '2px dashed #e2e8f0', borderRadius: 8, padding: '24px',
                    textAlign: 'center', cursor: 'pointer',
                  }}
                  onClick={() => document.getElementById('doc-upload')?.click()}
                >
                  <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
                  <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Click to upload documents</p>
                  <input
                    id="doc-upload"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => setNewDocs(Array.from(e.target.files || []))}
                  />
                </div>
                {newDocs.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {newDocs.map((f, i) => (
                      <div key={i} style={{ fontSize: 13, color: '#374151', padding: '4px 0' }}>
                        📄 {f.name}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </Col>

            {/* Right column — images & meta */}
            <Col lg={4}>
              {/* Featured Image */}
              <Card>
                <SectionHeader title="Featured Image" />
                <ImageUploadBox
                  preview={featuredPreview}
                  label="Featured Image"
                  onSelect={f => handleImageSelect(f, setFeaturedFile, setFeaturedPreview)}
                  inputId="featured-image"
                />
              </Card>

              {/* Poster Image */}
              <Card>
                <SectionHeader title="Event Poster" />
                <ImageUploadBox
                  preview={posterPreview}
                  label="Poster Image"
                  onSelect={f => handleImageSelect(f, setPosterFile, setPosterPreview)}
                  inputId="poster-image"
                />
              </Card>

              {/* Summary of toggles */}
              <Card>
                <SectionHeader title="Feature Summary" />
                <div style={{ fontSize: 13 }}>
                  {[
                    { label: 'Registration', on: form.registration_required },
                    { label: 'Manual Approval', on: form.require_manual_approval },
                    { label: 'Payment Proof', on: form.require_proof_of_payment },
                    { label: 'ID Verification', on: form.require_id_verification },
                    { label: 'Facial Verification', on: form.require_facial_verification },
                    { label: 'Paid Event', on: !form.is_free },
                    { label: 'Virtual', on: form.is_virtual },
                  ].map(item => (
                    <div key={item.label} style={{
                      display: 'flex', justifyContent: 'space-between',
                      padding: '6px 0', borderBottom: '1px solid #f1f5f9',
                    }}>
                      <span style={{ color: '#64748b' }}>{item.label}</span>
                      <span style={{
                        fontWeight: 600,
                        color: item.on ? '#16a34a' : '#94a3b8',
                      }}>
                        {item.on ? '✓ On' : '– Off'}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Submit */}
          <div style={{
            position: 'sticky', bottom: 0, background: '#fff',
            padding: '16px 0', borderTop: '1px solid #e2e8f0',
            display: 'flex', gap: 12, justifyContent: 'flex-end',
            marginTop: 16,
          }}>
            <button
              type="button"
              onClick={() => navigate('/events')}
              style={{
                background: '#f1f5f9', border: 'none', borderRadius: 8,
                padding: '10px 24px', fontSize: 14, cursor: 'pointer', color: '#374151', fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                background: '#00579a', color: '#fff', border: 'none',
                borderRadius: 8, padding: '10px 28px', fontSize: 14,
                fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {submitting && <Spinner animation="border" size="sm" />}
              {isEdit ? 'Save Changes' : 'Create Event'}
            </button>
          </div>
        </form>
      </Container>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    background: '#fff', borderRadius: 12, padding: '20px 24px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16,
  }}>
    {children}
  </div>
);

const ImageUploadBox: React.FC<{
  preview: string;
  label: string;
  onSelect: (f: File) => void;
  inputId: string;
}> = ({ preview, label, onSelect, inputId }) => (
  <div>
    {preview ? (
      <div style={{ position: 'relative' }}>
        <img
          src={preview}
          alt={label}
          style={{ width: '100%', borderRadius: 8, objectFit: 'cover', maxHeight: 200 }}
        />
        <button
          type="button"
          onClick={() => document.getElementById(inputId)?.click()}
          style={{
            position: 'absolute', bottom: 8, right: 8,
            background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
            borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
          }}
        >
          Change
        </button>
      </div>
    ) : (
      <div
        style={{
          border: '2px dashed #e2e8f0', borderRadius: 8, padding: '32px 16px',
          textAlign: 'center', cursor: 'pointer',
          transition: 'border-color 0.15s',
        }}
        onClick={() => document.getElementById(inputId)?.click()}
        onMouseEnter={e => (e.currentTarget.style.borderColor = '#00579a')}
        onMouseLeave={e => (e.currentTarget.style.borderColor = '#e2e8f0')}
      >
        <div style={{ fontSize: 32, marginBottom: 8 }}>🖼️</div>
        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Click to upload</p>
        <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, marginBottom: 0 }}>JPG, PNG, WEBP</p>
      </div>
    )}
    <input
      id={inputId}
      type="file"
      accept="image/*"
      style={{ display: 'none' }}
      onChange={e => { if (e.target.files?.[0]) onSelect(e.target.files[0]); }}
    />
  </div>
);

// ─── Shared Styles ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  fontSize: 14,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '8px 12px',
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

export default EventFormPage;